import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  IconButton,
  Box,
  Divider,
  Fade,
} from '@mui/material';

import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  YouTube as YoutubeIcon,
  Movie as MovieIcon,
  Image as ImageIcon,
} from '@mui/icons-material';

import axios from 'axios';
import { toast } from 'react-toastify';

const initialState = {
  title: '',
  description: '',
  video_type: 'upload',
  youtube_url: '',
  file: null,
  thumbnail: null,
  category: '',
  position: 0,
  is_active: true,
};

const UploadGuideVideoModal = ({ open, onClose, onRefresh, videoToEdit }) => {
  const isEdit = Boolean(videoToEdit);

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(initialState);

  // =========================
  // LOAD DATA IN EDIT MODE
  // =========================
  useEffect(() => {
    if (videoToEdit) {
      setForm({
        title: videoToEdit.title || '',
        description: videoToEdit.description || '',
        video_type: videoToEdit.video_type || 'upload',
        youtube_url: videoToEdit.youtube_url || '',
        file: null,
        thumbnail: null,
        category: videoToEdit.category || '',
        position: videoToEdit.position || 0,
        is_active: videoToEdit.is_active ? true : false,
      });
    } else {
      setForm(initialState);
    }
  }, [videoToEdit]);

  // =========================
  // RESET
  // =========================
  const resetForm = () => setForm(initialState);

  // =========================
  // SUBMIT CREATE / UPDATE
  // =========================
  const handleSubmit = async () => {
    if (!form.title) return toast.error('Le titre est obligatoire');
    if (form.video_type === 'youtube' && !form.youtube_url)
      return toast.error('Lien YouTube requis');
    if (form.video_type === 'upload' && !form.file && !isEdit)
      return toast.error('Vidéo obligatoire');

    setLoading(true);

    try {
      const data = new FormData();

      Object.keys(form).forEach((key) => {
        if (key === 'file') {
          if (form.video_type === 'upload' && form.file) {
            data.append('video', form.file);
          }
        } else if (key === 'thumbnail') {
          if (form.thumbnail) {
            data.append('thumbnail', form.thumbnail);
          }
        } else if (key === 'is_active') {
          data.append(key, form[key] ? 1 : 0);
        } else if (form[key] !== null && form[key] !== undefined) {
          data.append(key, form[key]);
        }
      });

      const url = isEdit
        ? `${process.env.REACT_APP_API_URL}/admin/usage-guides/videos/update/${videoToEdit.id}`
        : `${process.env.REACT_APP_API_URL}/admin/usage-guides/videos`;

      await axios.post(url, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      toast.success(isEdit ? 'Vidéo mise à jour' : 'Vidéo publiée');

      onRefresh();
      onClose();
      resetForm();
    } catch (error) {
      console.log(error.response);

      if (error.response?.data?.errors) {
        Object.values(error.response.data.errors)
          .flat()
          .forEach((err) => toast.error(err));
      } else {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            'Erreur lors de la sauvegarde'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // UI
  // =========================
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      TransitionComponent={Fade}
      PaperProps={{
        sx: { borderRadius: '24px', p: 1 },
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
        }}
      >
        <Typography variant="h5" fontWeight={800}>
          {isEdit ? 'Modifier le guide vidéo' : 'Nouveau guide vidéo'}
        </Typography>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      <DialogContent sx={{ mt: 2 }}>
        <Stack spacing={3}>
          {/* TITLE */}
          <TextField
            fullWidth
            label="Titre"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />

          {/* DESCRIPTION */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          {/* TYPE */}
          <FormControl fullWidth>
            <InputLabel>Type vidéo</InputLabel>
            <Select
              value={form.video_type}
              onChange={(e) => setForm({ ...form, video_type: e.target.value })}
            >
              <MenuItem value="upload">
                <MovieIcon sx={{ mr: 1 }} /> Fichier local
              </MenuItem>
              <MenuItem value="youtube">
                <YoutubeIcon sx={{ mr: 1, color: 'red' }} /> YouTube
              </MenuItem>
            </Select>
          </FormControl>

          {/* SOURCE */}
          {form.video_type === 'youtube' ? (
            <TextField
              fullWidth
              label="URL YouTube"
              value={form.youtube_url}
              onChange={(e) =>
                setForm({ ...form, youtube_url: e.target.value })
              }
            />
          ) : (
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadIcon />}
            >
              {form.file ? form.file.name : 'Uploader vidéo'}
              <input
                type="file"
                hidden
                accept="video/*"
                onChange={(e) => setForm({ ...form, file: e.target.files[0] })}
              />
            </Button>
          )}

          {/* THUMBNAIL */}
          <Button
            component="label"
            variant="outlined"
            startIcon={<ImageIcon />}
          >
            {form.thumbnail ? 'Thumbnail sélectionné' : 'Ajouter thumbnail'}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) =>
                setForm({ ...form, thumbnail: e.target.files[0] })
              }
            />
          </Button>

          {/* CATEGORY */}
          <TextField
            fullWidth
            label="Catégorie"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          />
        </Stack>
      </DialogContent>

      {/* ACTIONS */}
     <DialogActions sx={{ p: 3, flexDirection: 'column', alignItems: 'stretch' }}>

  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
    <Button onClick={onClose}>Annuler</Button>

    <Button
      variant="contained"
      disabled={loading}
      onClick={handleSubmit}
    >
      {loading ? (
        <CircularProgress size={20} />
      ) : isEdit ? (
        'Mettre à jour'
      ) : (
        'Publier'
      )}
    </Button>
  </Box>

  {/* 👇 MESSAGE LOADING */}
  {loading && (
    <Typography
      variant="caption"
      sx={{
        mt: 1,
        color: 'text.secondary',
        textAlign: 'center',
        fontStyle: 'italic'
      }}
    >
      ⏳ Patientez… la vidéo est en cours d’upload et de traitement
    </Typography>
  )}

</DialogActions>
    </Dialog>
  );
};

export default UploadGuideVideoModal;
